import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Store, Palette, Package, CreditCard, Bell, Globe, Check, ChevronRight, ChevronLeft, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const steps = [
  { id: 'profile', title: 'Profil Toko', description: 'Atur nama dan deskripsi toko', icon: Store },
  { id: 'template', title: 'Pilih Template', description: 'Pilih tampilan toko Anda', icon: Palette },
  { id: 'products', title: 'Tambah Produk', description: 'Tambah produk pertama', icon: Package },
  { id: 'payment', title: 'Payment Gateway', description: 'Hubungkan Pakasir', icon: CreditCard },
  { id: 'notifications', title: 'Notifikasi', description: 'Setup WhatsApp/Email', icon: Bell },
  { id: 'domain', title: 'Custom Domain', description: 'Opsional: domain sendiri', icon: Globe }
];

const templates = [
  { id: 'modern', name: 'Modern Minimal', preview: '/templates/modern.png', color: '#2D6CDF' },
  { id: 'classic', name: 'Classic Store', preview: '/templates/classic.png', color: '#10B981' },
  { id: 'bold', name: 'Bold & Dynamic', preview: '/templates/bold.png', color: '#F59E0B' },
  { id: 'elegant', name: 'Elegant Premium', preview: '/templates/elegant.png', color: '#8B5CF6' }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState(null);
  const [storeForm, setStoreForm] = useState({ name: '', description: '', logo_url: '' });
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [productForm, setProductForm] = useState({ name: '', price: '', description: '' });
  const [pakasirForm, setPakasirForm] = useState({ slug: '', api_key: '' });

  useEffect(() => {
    const fetchOnboarding = async () => {
      try {
        const r = await api.get('/tenant/onboarding');
        setOnboardingData(r.data);
        if (r.data.tenant) {
          setStoreForm({
            name: r.data.tenant.name || '',
            description: r.data.tenant.description || '',
            logo_url: r.data.tenant.logo_url || ''
          });
          setSelectedTemplate(r.data.tenant.template || 'modern');
        }
        // Find first incomplete step
        const firstIncomplete = steps.findIndex(s => !r.data.steps[s.id]);
        setCurrentStep(firstIncomplete >= 0 ? firstIncomplete : 0);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchOnboarding();
  }, []);

  const progress = onboardingData ? (onboardingData.completed / onboardingData.total) * 100 : 0;

  const handleSaveProfile = async () => {
    if (!storeForm.name.trim()) return toast.error('Nama toko harus diisi');
    setSaving(true);
    try {
      await api.put('/tenant/store', storeForm);
      toast.success('Profil toko disimpan');
      nextStep();
    } catch (e) {
      toast.error('Gagal menyimpan profil');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/store', { template: selectedTemplate });
      toast.success('Template dipilih');
      nextStep();
    } catch (e) {
      toast.error('Gagal menyimpan template');
    } finally {
      setSaving(false);
    }
  };

  const handleAddProduct = async () => {
    if (!productForm.name.trim()) return toast.error('Nama produk harus diisi');
    setSaving(true);
    try {
      await api.post('/tenant/products', {
        name: productForm.name,
        price: parseInt(productForm.price) || 0,
        description: productForm.description,
        type: 'physical',
        is_active: true
      });
      toast.success('Produk ditambahkan');
      nextStep();
    } catch (e) {
      toast.error('Gagal menambah produk');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePakasir = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/settings', {
        pakasir: {
          slug: pakasirForm.slug,
          api_key: pakasirForm.api_key,
          mode: 'sandbox',
          is_active: !!pakasirForm.slug && !!pakasirForm.api_key
        }
      });
      toast.success('Pakasir terhubung');
      nextStep();
    } catch (e) {
      toast.error('Gagal menyimpan Pakasir');
    } finally {
      setSaving(false);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const skipStep = () => {
    nextStep();
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  const StepIcon = steps[currentStep].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="onboarding-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D6CDF]/10 rounded-full text-[#2D6CDF] text-sm font-medium mb-4">
            <Sparkles size={16} /> Setup Wizard
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Selamat Datang di Tokomoe!</h1>
          <p className="text-slate-500 mt-2">Mari siapkan toko online Anda dalam beberapa langkah mudah</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">Progress</span>
            <span className="text-sm font-medium text-[#2D6CDF]">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8 overflow-x-auto pb-2">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const isCompleted = onboardingData?.steps[step.id];
            const isCurrent = i === currentStep;
            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(i)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                  isCurrent ? 'bg-[#2D6CDF] text-white' :
                  isCompleted ? 'bg-emerald-100 text-emerald-700' :
                  'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {isCompleted && !isCurrent ? <Check size={14} /> : <Icon size={14} />}
                <span className="text-xs font-medium hidden sm:inline">{step.title}</span>
              </button>
            );
          })}
        </div>

        {/* Step Content */}
        <Card className="border-slate-200 shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#2D6CDF]/10 flex items-center justify-center">
                <StepIcon size={24} className="text-[#2D6CDF]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{steps[currentStep].title}</h2>
                <p className="text-sm text-slate-500">{steps[currentStep].description}</p>
              </div>
            </div>

            {/* Step 1: Profile */}
            {currentStep === 0 && (
              <div className="space-y-4">
                <div>
                  <Label>Nama Toko</Label>
                  <Input value={storeForm.name} onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })} placeholder="Nama toko Anda" data-testid="store-name-input" />
                </div>
                <div>
                  <Label>Deskripsi Toko</Label>
                  <Textarea value={storeForm.description} onChange={(e) => setStoreForm({ ...storeForm, description: e.target.value })} placeholder="Ceritakan tentang toko Anda..." rows={3} data-testid="store-description-input" />
                </div>
                <div>
                  <Label>URL Logo (opsional)</Label>
                  <Input value={storeForm.logo_url} onChange={(e) => setStoreForm({ ...storeForm, logo_url: e.target.value })} placeholder="https://..." data-testid="store-logo-input" />
                </div>
                <Button onClick={handleSaveProfile} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-profile-btn">
                  {saving && <Loader2 size={14} className="animate-spin mr-2" />}
                  Simpan & Lanjut <ChevronRight size={16} className="ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Template */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {templates.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTemplate(t.id)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        selectedTemplate === t.id ? 'border-[#2D6CDF] bg-[#2D6CDF]/5' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`template-${t.id}`}
                    >
                      <div className="h-24 rounded bg-gradient-to-br from-slate-100 to-slate-200 mb-3 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${t.color}20, ${t.color}10)` }}>
                        <Palette size={32} style={{ color: t.color }} />
                      </div>
                      <p className="font-medium text-slate-900">{t.name}</p>
                      {selectedTemplate === t.id && <Check size={16} className="text-[#2D6CDF] mt-1" />}
                    </button>
                  ))}
                </div>
                <Button onClick={handleSaveTemplate} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-template-btn">
                  {saving && <Loader2 size={14} className="animate-spin mr-2" />}
                  Pilih Template <ChevronRight size={16} className="ml-2" />
                </Button>
              </div>
            )}

            {/* Step 3: Product */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Nama Produk</Label>
                  <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="Contoh: Kaos Polos" data-testid="product-name-input" />
                </div>
                <div>
                  <Label>Harga (Rp)</Label>
                  <Input type="number" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} placeholder="99000" data-testid="product-price-input" />
                </div>
                <div>
                  <Label>Deskripsi (opsional)</Label>
                  <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} placeholder="Deskripsi produk..." rows={2} />
                </div>
                <Button onClick={handleAddProduct} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="add-product-btn">
                  {saving && <Loader2 size={14} className="animate-spin mr-2" />}
                  Tambah Produk <ChevronRight size={16} className="ml-2" />
                </Button>
                <Button onClick={skipStep} variant="ghost" className="w-full" data-testid="skip-product-btn">
                  Lewati untuk sekarang
                </Button>
              </div>
            )}

            {/* Step 4: Pakasir */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">Pakasir adalah payment gateway untuk menerima pembayaran. Daftar di <a href="https://pakasir.com" target="_blank" rel="noreferrer" className="underline font-medium">pakasir.com</a> jika belum punya akun.</p>
                </div>
                <div>
                  <Label>Project Slug</Label>
                  <Input value={pakasirForm.slug} onChange={(e) => setPakasirForm({ ...pakasirForm, slug: e.target.value })} placeholder="my-store" data-testid="pakasir-slug-input" />
                </div>
                <div>
                  <Label>API Key</Label>
                  <Input type="password" value={pakasirForm.api_key} onChange={(e) => setPakasirForm({ ...pakasirForm, api_key: e.target.value })} placeholder="xxx123..." data-testid="pakasir-api-input" />
                </div>
                <Button onClick={handleSavePakasir} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-pakasir-btn">
                  {saving && <Loader2 size={14} className="animate-spin mr-2" />}
                  Hubungkan Pakasir <ChevronRight size={16} className="ml-2" />
                </Button>
                <Button onClick={skipStep} variant="ghost" className="w-full" data-testid="skip-pakasir-btn">
                  Lewati untuk sekarang
                </Button>
              </div>
            )}

            {/* Step 5: Notifications */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <p className="text-slate-600">Anda bisa mengatur notifikasi WhatsApp dan Email nanti di menu Settings.</p>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                        <Bell size={20} className="text-emerald-600" />
                      </div>
                      <p className="font-medium">WhatsApp</p>
                      <p className="text-xs text-slate-500">via Responic</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
                        <Bell size={20} className="text-violet-600" />
                      </div>
                      <p className="font-medium">Email</p>
                      <p className="text-xs text-slate-500">via Myerketing</p>
                    </CardContent>
                  </Card>
                </div>
                <Button onClick={nextStep} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="continue-notifications-btn">
                  Lanjut <ChevronRight size={16} className="ml-2" />
                </Button>
              </div>
            )}

            {/* Step 6: Domain */}
            {currentStep === 5 && (
              <div className="space-y-4">
                <p className="text-slate-600">Toko Anda sudah bisa diakses di:</p>
                <div className="p-4 bg-[#2D6CDF]/5 rounded-lg border border-[#2D6CDF]/20">
                  <p className="font-mono text-[#2D6CDF] font-medium">{onboardingData?.tenant?.subdomain || 'nama-toko'}.tokomoe.com</p>
                </div>
                <p className="text-sm text-slate-500">Anda juga bisa menambahkan custom domain nanti di menu Domain Manager.</p>
                <Button onClick={goToDashboard} className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="finish-onboarding-btn">
                  <Check size={16} className="mr-2" /> Selesai! Ke Dashboard
                </Button>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-100">
              <Button variant="ghost" onClick={prevStep} disabled={currentStep === 0}>
                <ChevronLeft size={16} className="mr-1" /> Kembali
              </Button>
              <Button variant="ghost" onClick={goToDashboard}>
                Ke Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
